import React from "react";
import { format } from "date-fns";
import { Column } from "components/Layout/Grid";
import Text from "components/Text";
import { Button } from "components/Button";
import { currencyOutputNumber } from "utils/CurrencyOutput";
import type { ILostItemRow } from "../ModalLostItems/LostItemsDetailDropdown";

export interface IWonItemDetailSubItem {
  quotationId?: string;
  quotationCode?: string;
  codeQuotation?: string;
  createdAt?: string;
  customerCode?: string;
  razaoSocial?: string;
  category?: string;
  sentPrice?: string | number;
  origin?: number;
  quotationOrigin?: number;
  status?: string;
  supplierStatus?: string;
}

export interface IWonItemRow {
  code?: string;
  description?: string;
  brand?: string;
  subItems?: IWonItemDetailSubItem[];
}

const DETAIL_COLUMNS = [
  { key: "quotationCode", label: "Cotação" },
  { key: "createdAt", label: "Criação" },
  { key: "customerCode", label: "C. Cliente" },
  { key: "razaoSocial", label: "Razão Social" },
  { key: "category", label: "Categoria" },
  { key: "sentPrice", label: "Preço enviado" },
  { key: "origin", label: "Origem" },
] as const;

/** Colunas em fr para ocupar todo o espaço disponível (Razão Social com mais espaço) */
const gridTemplateColumns = "1fr 1fr 1fr 2fr 1.5fr 1fr 1fr";

function getDetailCellValue(item: IWonItemDetailSubItem, key: string): string {
  if (key === "createdAt") {
    return item.createdAt
      ? format(new Date(item.createdAt), "dd/MM/yyyy' às 'HH:mm")
      : "-";
  }

  if (key === "sentPrice") {
    return item.sentPrice !== undefined && item.sentPrice !== null
      ? currencyOutputNumber(Number(item.sentPrice))
      : "-";
  }

  if (key === "origin") {
    const o = item.quotationOrigin ?? item.origin;
    return o === 0 ? "Bionexo" : "MVM";
  }

  const value = item[key as keyof IWonItemDetailSubItem];
  return value !== undefined && value !== null ? String(value) : "-";
}

function subItemToQuotationRow(
  _parent: IWonItemRow,
  sub: IWonItemDetailSubItem
): ILostItemRow {
  return {
    quotationId: sub.quotationId,
    codeQuotation: sub.codeQuotation ?? sub.quotationCode,
    customerCode: sub.customerCode,
    quotationOrigin:
      sub.quotationOrigin !== undefined && sub.quotationOrigin !== null
        ? sub.quotationOrigin
        : sub.origin,
    status: sub.status,
    supplierStatus: sub.supplierStatus,
  };
}

export type WonItemsDetailDropdownProps = IWonItemRow & {
  onOpenQuotation?: (row: ILostItemRow) => void;
};

export const WonItemsDetailDropdown: React.FC<WonItemsDetailDropdownProps> = (props) => {
  const { onOpenQuotation, ...row } = props;
  const subItems = row.subItems ?? [];

  return (
    <Column
      className="animated fadeIn bg-white py-3"
      style={{
        marginLeft: 0,
        width: "calc(100% - 17px)",
        paddingLeft: 20,
        paddingRight: 20,
      }}
    >
      <Text className="font-bold color-blue-gray mt-2 mb-3" size={12}>
        {subItems.length > 0 ? "" : "Sem itens para exibir"}
      </Text>
      {subItems.length > 0 && (
        <div className="won-items-detail-grid" style={{ width: "100%" }}>
          <div
            className="won-items-detail-grid-row won-items-detail-grid-header"
            style={{ display: "grid", gridTemplateColumns }}
          >
            {DETAIL_COLUMNS.map((col, colIdx) => (
              <div
                key={col.key}
                className="d-flex align-center py-2 px-2 text-left font-bold"
                style={{
                  minWidth: 0,
                  backgroundColor: "#D0DEE1",
                  color: "#2d3748",
                  fontSize: 12,
                  borderTopLeftRadius: colIdx === 0 ? 8 : 0,
                  borderTopRightRadius: colIdx === DETAIL_COLUMNS.length - 1 ? 8 : 0,
                }}
              >
                {col.label}
              </div>
            ))}
          </div>
          {subItems.map((item, idx) => (
            <div
              key={idx}
              className="won-items-detail-grid-row"
              style={{
                display: "grid",
                gridTemplateColumns,
                minHeight: 50,
                alignItems: "center",
                backgroundColor: idx % 2 === 0 ? "#ffffff" : "#F5F7F8",
              }}
            >
              {DETAIL_COLUMNS.map((col) => (
                <div
                  key={col.key}
                  className="color-blue-gray text-left py-2 px-2"
                  style={{ minWidth: 0, fontSize: 12 }}
                >
                  {onOpenQuotation ? (
                    <Button
                      width="100%"
                      className="text-left color-primary w-100 p-0 justify-start"
                      style={{ minWidth: 0, fontSize: 12 }}
                      onClick={() =>
                        onOpenQuotation(subItemToQuotationRow(row, item))
                      }
                    >
                      <span className="color-primary">
                        {getDetailCellValue(item, col.key)}
                      </span>
                    </Button>
                  ) : (
                    getDetailCellValue(item, col.key)
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Column>
  );
};
